import React, { useState, useEffect, useContext } from 'react';
import { NotificationContext } from '../../context/NotificationContext';
import api from '../../api';

const ProviderManager = ({ configId }) => {
  const { showNotification } = useContext(NotificationContext);
  const [providers, setProviders] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    display_name: '',
    phone_number: '',
    email: '',
    provider_type_id: '',
    request_type_ids: []
  });

  useEffect(() => {
    loadProviders();
    loadProviderTypes();
    loadRequestTypes();
  }, [configId]);

  const loadProviders = async () => {
    try {
      const response = await api.get(`/admin/providers/${configId}`);
      setProviders(response.data);
    } catch (error) {
      showNotification('Error loading providers: ' + error.message, true);
    }
  };

  const loadProviderTypes = async () => {
    try {
      const response = await api.get('/enum/provider-type/');
      setProviderTypes(response.data);
    } catch (error) {
      showNotification('Error loading provider types: ' + error.message, true);
    }
  };

  const loadRequestTypes = async () => {
    try {
      const response = await api.get(`/admin/request_types/${configId}`);
      setRequestTypes(response.data);
    } catch (error) {
      showNotification('Error loading request types: ' + error.message, true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submissionData = {
        configuration_id: parseInt(configId),
        first_name: formData.first_name,
        last_name: formData.last_name,
        display_name: formData.display_name || undefined,
        phone_number: formData.phone_number || undefined,
        email: formData.email || undefined,
        provider_type_id: formData.provider_type_id ? parseInt(formData.provider_type_id) : undefined,
        request_type_ids: formData.request_type_ids.map(id => parseInt(id))
      };

      await api.post('/admin/providers/', submissionData);
      showNotification('Provider added successfully!');
      setIsModalOpen(false);
      setFormData({
        first_name: '',
        last_name: '',
        display_name: '',
        phone_number: '',
        email: '',
        provider_type_id: '',
        request_type_ids: []
      });
      loadProviders();
    } catch (error) {
      showNotification('Failed to add provider: ' + error.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this provider?')) return;

    try {
      await api.delete(`/admin/providers/${id}`);
      showNotification('Provider deleted successfully!');
      loadProviders();
    } catch (error) {
      showNotification('Failed to delete provider: ' + error.message, true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'request_type_ids') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData(prev => ({ ...prev, [name]: selectedOptions }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const getProviderTypeName = (typeId) => {
    const type = providerTypes.find(t => t.id === typeId);
    return type ? type.name : '-';
  };

  const getRequestTypeNames = (providerRequestTypes) => {
    if (!providerRequestTypes || providerRequestTypes.length === 0) return '-';
    return providerRequestTypes
      .map(rt => {
        const type = requestTypes.find(t => t.id === rt.id);
        return type ? type.name : '';
      })
      .filter(name => name)
      .join(', ');
  };

  return (
    <div className="section-card">
      <div className="section-header">
        <h2>Providers</h2>
        <button 
          className="add-entity-btn"
          onClick={() => setIsModalOpen(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
          </svg>
          <span>Add Provider</span>
        </button>
      </div>
      
      <div className="section-content">
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Display Name</th>
                <th>Provider Type</th>
                <th>Request Types</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {providers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-gray-500">
                    No providers found.
                  </td>
                </tr>
              ) : (
                providers.map(provider => (
                  <tr key={provider.id}>
                    <td>{provider.first_name} {provider.last_name}</td>
                    <td>{provider.display_name || '-'}</td>
                    <td>{getProviderTypeName(provider.provider_type_id)}</td>
                    <td>{getRequestTypeNames(provider.request_types)}</td>
                    <td>{provider.phone_number || '-'}</td>
                    <td>{provider.email || '-'}</td>
                    <td>
                      <button 
                        onClick={() => handleDelete(provider.id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="section-modal">
          <button 
            className="modal-close"
            onClick={() => setIsModalOpen(false)}
          >
            ×
          </button>
          <h3>Add Provider</h3>
          <form onSubmit={handleSubmit}>
            <div>
              <label className="form-label">First Name</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="form-label">Last Name</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="form-label">Display Name</label>
              <input
                type="text"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Optional display name"
              />
            </div>
            <div>
              <label className="form-label">Provider Type</label>
              <select
                name="provider_type_id"
                value={formData.provider_type_id}
                onChange={handleInputChange}
                className="form-input"
                required
              >
                <option value="">Select a provider type</option>
                {providerTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Request Types</label>
              <select
                name="request_type_ids"
                value={formData.request_type_ids}
                onChange={handleInputChange}
                className="form-input"
                multiple
                size="4"
              >
                {requestTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
              <p className="text-sm text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
            </div>
            <div>
              <label className="form-label">Phone</label>
              <input
                type="tel"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setIsModalOpen(false)}>
                Cancel
              </button>
              <button type="submit" disabled={loading}>
                {loading ? 'Adding...' : 'Add Provider'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ProviderManager; 